import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { RetailerTypesService } from '@emi/shared/data-access';
import * as retailerTypeActions from './retailer-type-actions';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { RetailerTypeState } from './retailer-type-reducer';
import { getRetailerTypeState } from './retailer-type-selectors';
import { RetailerTypeDto, Filters } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class RetailerTypeEffects {
    private currentState: RetailerTypeState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private retailerTypesService: RetailerTypesService,
        private retailerTypeStore: Store<RetailerTypeState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.retailerTypeStore.pipe(select(getRetailerTypeState)).subscribe((x) => (this.currentState = x));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getRetailerTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.getRetailerTypes),
            map(() => {
                return retailerTypeActions.isLoadingRetailerTypes();
            })
        )
    );

    getRetailerTypesNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.getNextPageRetailerTypes),
            switchMap(() => {
                return this.retailerTypesService
                    .getRetailerTypes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            searchText: this.currentState.searchText ?? undefined,
                        })
                    )
                    .pipe(
                        map((data: RetailerTypeDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return retailerTypeActions.updateRetailerTypesSelectionSuccess({ retailers: newPage, metadata: data.metadata });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerTypeActions.getRetailerTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.amendRetailerTypesSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.retailerTypesService
                    .getRetailerTypes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((retailerTypesResponse) => {
                            const amendedRetailerTypes = this.currentFilters.retailerTypes.filter(
                                (rt) => !!retailerTypesResponse.results.find((retailerType) => retailerType.id == rt.id)
                            );
                            return dataExtractionActions.addRetailerTypeSelection({ retailerTypes: amendedRetailerTypes });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addRetailerTypeSelection({ retailerTypes: [] }));
                            }
                            return of(retailerTypeActions.getRetailerTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchRetailerTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.fetchRetailerTypes),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.retailerTypes.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            retailerTypeActions.getRetailerTypesSuccess({
                                retailerTypes: this.currentState.retailerTypes,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText ?? '',
                            })
                        );
                    }
                }
                return this.retailerTypesService
                    .getRetailerTypes(createFilterDto({ filterState: this.currentFilters, searchTerm: this.includeSearchPhrase? this.searchTermState : undefined}))
                    .pipe(
                        map((data: RetailerTypeDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return retailerTypeActions.getRetailerTypesSuccess({
                                retailerTypes: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText ?? '',
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerTypeActions.getRetailerTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    searchRetailerTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.searchRetailerTypes),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.retailerTypesService
                    .getRetailerTypes(
                        createFilterDto({
                          filterState: currentFilters,
                          searchText: action.searchText,
                          searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: RetailerTypeDto) => {
                            this.previousFilters = currentFilters;
                            return retailerTypeActions.getRetailerTypesSuccess({
                                retailerTypes: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerTypeActions.getRetailerTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerTypeActions.isLoadingRetailerTypes),
            map(() => {
                return retailerTypeActions.fetchRetailerTypes();
            })
        )
    );
}
